import $ from 'jquery';
import CurrencyConverter from 'shopify-currency-converter';
import rimg from 'rimg-shopify';
import Cart from './Cart';
import ScrollLock from '../utils/ScrollLock';
import Layout from '../Layout';

export default class HeaderMiniCart {
  constructor(section) {
    this.section = section;
    this.$el = $(section.el);
    this.el = section.el;
    this.moneyFormat = section.data.moneyFormat;

    // Mini-cart
    this.$drawer = $('[data-header-minicart-drawer]', this.$el);
    this._toggleDrawer = this._toggleDrawer.bind(this);
    this.$title = $('[data-header-minicart-title]', this.$el);
    this.titleText = this.$title.data('header-minicart-title');
    this.open = false;

    // Event handlers
    this.events = [
      $('[data-site-header-minicart-dismiss]', this.$el).on('click.minicart', this._toggleDrawer),
      this.$drawer.on('touchmove.minicart', (event) => {
        // this allows scrolling within the minicart while page is scroll-locked.
        event.stopPropagation();
      }),
      $(window).on('resize.minicart', () => this._setMaxDrawerHeight()),
    ];

    Layout.onBreakpointChange(() => this._toggleDrawer('closed'));

    Cart.addCart(section.el, section.postMessage);

    // Check if position: sticky is supported, and add alternative styling and event handlers if not
    if (window.getComputedStyle(this.el.querySelector('.header-minicart-header')).position.indexOf('sticky') === -1) {
      this._makeItWorkWithoutSticky();
    }
  }

  _calculateMaxDrawerHeight() {
    const {
      top: headerTop,
      height: headerHeight,
    } = document.querySelector('#shopify-section-static-header').getBoundingClientRect();

    const drawerOffset = Math.max(headerHeight + headerTop, 0);
    if (this.workingWithoutSticky) {
      // IE11 doesn't transition maxHeight properly for vh units
      return `${window.innerHeight - drawerOffset}px`;
    }
    return `calc(100vh - ${drawerOffset}px)`;
  }

  _makeItWorkWithoutSticky() {
    this.workingWithoutSticky = true;

    Layout.onBreakpointChange(() => {
      if (!Layout.isBreakpoint('L')) {
        this.el.style.top = `${window.pageYOffset}px`;
      }
    });

    const { style: drawerStyle } = this.$drawer[0];
    drawerStyle.width = '100%';
    drawerStyle.left = '50%';
    drawerStyle.transform = 'translate(-50%)';

    this.el.style.position = 'absolute';
    this.el.style.left = 0;
    this.el.style.right = 0;

    const header = document.querySelector('#shopify-section-static-header');
    const setTop = () => {
      const headerBounds = header.getBoundingClientRect();
      if (Layout.isBreakpoint('L')) {
        this.el.style.top = `${Math.max(0, headerBounds.top + headerBounds.height) + window.pageYOffset}px`;
      } else {
        this.el.style.top = `${window.pageYOffset}px`;
      }
    };

    setTop();
    this.events.push($(window).on('scroll.minicart', setTop));
  }

  _setScrollLock() {
    if (!this.open) {
      return;
    }

    if (this.workingWithoutSticky) {
      ScrollLock.lock();
      return;
    }

    if (!Layout.isBreakpoint('L')) {
      ScrollLock.lock();
      return;
    }

    const { scrollHeight, clientHeight } = this.$drawer[0];
    // If cart drawer is smaller than full screen, allow scrolling.
    if (scrollHeight <= clientHeight) {
      ScrollLock.unlock();
    } else {
      ScrollLock.lock();
    }
  }

  _setMaxDrawerHeight(force = false) {
    if (!this.open && !force) {
      return;
    }

    if (!Layout.isBreakpoint('L')) {
      // because Chrome on Android broke 100vh sizing, this needs to be set dynamically
      this.$drawer[0].style.maxHeight = `${window.innerHeight}px`;
      return;
    }

    this.$drawer[0].style.maxHeight = this._calculateMaxDrawerHeight();
  }

  _unsetMaxDrawerHeight() {
    this.$drawer[0].style.maxHeight = null;
  }

  _refreshCart() {
    this.$drawer[0].classList.add('loading');
    this._blankMiniCartTotals();
    Cart.update().done((data) => {
      this._onMiniCartUpdate(data);
      this._setScrollLock();
      this.section.postMessage('cart:update', { data });
    });
  }

  _openDrawer() {
    this._setMaxDrawerHeight(true);
    this.$drawer.one('transitionend', () => this._setScrollLock());
    this.$drawer.parent()[0].classList.add('open');
    this._addClickToCloseEvents();
    this.open = true;
  }

  _toggleDrawer(requestedState) {
    if (requestedState === 'open' && this.open) {
      this._refreshCart();
      return;
    }
    if (requestedState === 'closed' && !this.open) {
      return;
    }
    if (this.open) {
      this.$drawer.one('transitionend', () => {
        this.section.postMessage('header-minicart:close');
      });

      ScrollLock.unlock();
      this.$drawer.parent()[0].classList.remove('open');
      this._unsetMaxDrawerHeight();
      this._removeClickToCloseEvents();
      this.open = false;
    } else {
      let waitingOnTransition = false;

      // If there is a bottom menu initialized, it will use this callback to delay opening
      // until the $.deferred transitionEnded has resolved.
      this.section.postMessage('header-minicart:open', (transitionEnded) => {
        waitingOnTransition = true;
        this._refreshCart();
        transitionEnded.done(() => this._openDrawer());
      });

      // otherwise, if the callback is never invoked, we can just open directly.
      // Because postMessage is synchronous, we know that by this point
      // anything that should will have invoked the callback.
      if (!waitingOnTransition) {
        this._refreshCart();
        this._openDrawer();
      }
    }
  }

  _addClickToCloseEvents() {
    $(window).on('click.minicart', () => this._toggleDrawer('closed'));
    this.$drawer.on('click.minicart', event => event.stopPropagation());
  }

  _removeClickToCloseEvents() {
    $(window).off('click.minicart');
    this.$drawer.off('click.minicart');
  }

  _onMiniCartUpdate(cartData) {
    this.$drawer[0].classList.remove('loading');
    rimg.watch(this.$drawer[0]);
  }

  _updateMiniCartTotals(cartData) {
    if (cartData.options.item_count === 0) {
      this.$drawer[0].classList.add('minicart-cart-empty');
      return;
    }

    this.$drawer[0].classList.remove('minicart-cart-empty');
    this.$title.text(`${this.titleText} (${cartData.options.item_count})`);
  }

  _blankMiniCartTotals() {
    this.$title.text(this.titleText);
  }

  onSectionUnload() {
    Cart.unload();
    this._removeClickToCloseEvents();
    this.events.forEach($el => $el.off('.minicart'));
  }

  onSectionMessage(name, data) {
    if (name === 'cart:update') {
      this._updateMiniCartTotals(data.data);
    }
    if (name === 'header-minicart:toggle') {
      this._toggleDrawer(data);
    }
    if (name === 'header-minicart:set-open') {
      this._toggleDrawer('open');
    }
  }
}
